/**
 * The deal view of one saved result, laid out by the model's own layout
 * (`deal-model-layout-v1`): the verdict band (verdict, reason, bid sentence,
 * input provenance), the KPI strip with the maximum sensible bid, the
 * authored sections as panels (summary rows, checks, assumptions) on the same
 * maximum-bid basis, plus a separate listing-price comparison. Used for every model except Home buyer, and only
 * when the server sends a layout; without one the flat breakdown remains.
 * A saved result that is not the latest calculation carries the stale mark
 * beside the verdict, and the reason line says it is the last saved one and why.
 */
import { useId, type ReactNode } from 'react'
import type { DealModelLayout, DealModelLayoutInput, DealModelLayoutSection } from '../../../api/dealModelLayout'
import type { BidEvaluation, DealsV2PropertyModelChoice, PresentationOutput } from '../../../api/financial'
import { verdictBand, verdictTone } from '../../deal-models/composer/verdictTone'
import { Pill, Verdict } from '../../../ui/kit'
import { lastSavedResultLine, StaleMark } from '../../../ui/StaleMark'
import {
  bidConstraint, bidSentence, bidVersusPrice, checkRow, checksById, formatInputValue, inputsByTier, kpiOutputIds,
  resolveInput, SOURCE_LABEL, verdictReason, type CheckRow, type InputContext, type InputSource, type ResultLookups,
} from './dealLayout'
import { bidStateLabel, formatBidPence, formatOutput, humanize, valueAt, type FinancialModelResult } from './format'
import { FinancialOutputValue, type GdvEditingContext } from './PersonalGdv'

export function SourceChip({ source }: { source: InputSource }) {
  return <span className="fin-src" data-source={source}>{SOURCE_LABEL[source]}</span>
}

/** ✓ / ✕ as text with an accessible name, never colour alone. */
export function CheckMark({ passes }: { passes: boolean | null }) {
  return passes === true ? <Pill tone="good"><span role="img" aria-label="passes">✓</span></Pill>
    : passes === false ? <Pill tone="bad"><span role="img" aria-label="fails">✕</span></Pill>
      : <Pill muted><span role="img" aria-label="not evaluated">–</span></Pill>
}

function verdictValue(layout: DealModelLayout, result: FinancialModelResult, lookups: ResultLookups): string | null {
  const verdict = layout.verdict
  if (!verdict) return null
  const saved = lookups.outputs.has(verdict.outputId) ? lookups.value(verdict.outputId) : result.result ? valueAt(result.result, `/deal/${verdict.key}`) : undefined
  return typeof saved === 'string' ? saved : null
}

/* ---------- verdict band ---------- */

function VerdictBand({ layout, result, lookups, context, priceLabel, status, notice, updating, staleReasons, recalculating }: {
  layout: DealModelLayout; result: FinancialModelResult; lookups: ResultLookups; context: InputContext; priceLabel: string; status: ReactNode; notice?: ReactNode; updating: boolean
  staleReasons: readonly string[]; recalculating: boolean
}) {
  const value = verdictValue(layout, result, lookups)
  const tiers = (layout.verdict?.tiers ?? []).map(tier => ({ value: tier.value, conditionZen: null }))
  const band = verdictBand(verdictTone(value, tiers))
  // The reason pairs saved measures with current thresholds, so it is only stated for a current result. A result
  // that is not the latest says it is the last saved one and why (it does not carry the inputs it used).
  const reason = updating ? lastSavedResultLine(staleReasons, recalculating) : verdictReason(layout, value, lookups, context)
  const staleMark = updating ? <StaleMark reasons={staleReasons} updating={recalculating} /> : null
  const evaluation = result.bidEvaluation
  const constraint = evaluation ? bidConstraint(layout, lookups, evaluation) : null
  const primary = inputsByTier(layout).primary
  return <section className="fin-verdict" aria-label="Verdict">
    <div className="fin-verdict-head">
      {layout.verdict ? <span className="fin-verdict-chip"><Verdict band={band}>{value ? humanize(value) : 'Not computable'}</Verdict>{staleMark}</span> : null}
      <p className="fin-verdict-reason">{reason}</p>
      {layout.verdict || !staleMark ? status : <span className="fin-verdict-chip">{staleMark}{status}</span>}
    </div>
    {evaluation ? <div className="fin-verdict-bid" data-testid="financial-bid-summary">
      <p>{bidSentence(evaluation, priceLabel, layout.solver?.label)}</p>
      {constraint ? <p className="fin-faint">{constraint.label}: {constraint.value}</p> : null}
    </div> : null}
    {primary.length ? <ul className="fin-prov" aria-label="Where the main inputs come from">{primary.map(input => {
      const resolved = resolveInput(input, context)
      return <li key={input.key}><span>{input.label}</span><SourceChip source={resolved.source} /></li>
    })}</ul> : null}
    {notice}
  </section>
}

/* ---------- KPI strip ---------- */

function KpiStrip({ layout, result, lookups, modelId, gdvEditor, pricePence, priceLabel, updating, hideTargetKpis }: {
  layout: DealModelLayout; result: FinancialModelResult; lookups: ResultLookups; modelId: string; gdvEditor?: GdvEditingContext; pricePence: number | null | undefined; priceLabel: string; updating: boolean; hideTargetKpis?: boolean
}) {
  const targetOutputIds = new Set(hideTargetKpis ? [layout.returnTargets?.margin, layout.returnTargets?.profit].flatMap(binding => binding ? [binding.outputId] : []) : [])
  const ids = kpiOutputIds(layout, result.presentation).filter(id => !targetOutputIds.has(id))
  const bid = result.bidEvaluation?.bid
  const maximum = bid?.state === 'computed' ? bid.maxBidPence : null
  const versus = updating ? null : bidVersusPrice(maximum, pricePence)
  const bidNote = !bid ? null : bid.state !== 'computed' ? bidStateLabel(bid.state) : updating ? 'Last saved' : versus ? (versus.direction === 'at' ? `At the ${priceLabel.toLowerCase()}` : `${versus.percent} ${versus.direction} the ${priceLabel.toLowerCase()}`) : null
  if (!ids.length && !bid) return null
  return <dl className="figs fin-kpis" aria-label="Key results">
    {ids.map(id => {
      const output = lookups.outputs.get(id)!
      const raw = lookups.value(id)
      return <div className="fig" key={id}><dt><small>{output.label}</small></dt><dd><b><FinancialOutputValue modelId={modelId} outputId={id} value={formatOutput(raw, output)} rawValue={raw} gdvEditor={gdvEditor} /></b></dd></div>
    })}
    {bid ? <div className="fig k fin-kpi-bid"><dt><small>{layout.solver?.label ?? 'Maximum sensible bid'}</small></dt><dd>
      <b data-testid="financial-maximum-bid">{maximum ? formatBidPence(maximum) : '—'}</b>
      {bidNote ? <span className="g2">{bidNote}</span> : null}
    </dd></div> : null}
  </dl>
}

/* ---------- sections ---------- */

function CheckLine({ row }: { row: CheckRow }) {
  return <div className="fin-check" data-pass={row.passes === null ? undefined : String(row.passes)}>
    <dt><CheckMark passes={row.passes} /><span>{row.label}</span></dt>
    <dd>{row.left !== null && row.right !== null ? <>{row.left} <span className="fin-check-op" aria-hidden="true">{row.comparator}</span>{row.comparatorPhrase ? <span className="sr-only">, {row.comparatorPhrase}</span> : null} {row.right}</> : row.passes === null ? '—' : row.passes ? 'Yes' : 'No'}</dd>
  </div>
}

const TIER_NOTE: Record<DealModelLayoutInput['tier'], string | null> = { primary: null, assumption: 'assumption', target: 'target' }

function InputLine({ input, context }: { input: DealModelLayoutInput; context: InputContext }) {
  const resolved = resolveInput(input, context)
  const note = TIER_NOTE[input.tier]
  return <div className="fin-row is-input"><dt>{input.label}{note ? <> <span className="fin-assume">{note}</span></> : null}</dt><dd>{formatInputValue(input, resolved.value)} <SourceChip source={resolved.source} /></dd></div>
}

function SectionPanel({ section, layout, lookups, context, modelId, gdvEditor, checks }: {
  section: Pick<DealModelLayoutSection, 'key' | 'label' | 'description' | 'items'>; layout: DealModelLayout; lookups: ResultLookups; context: InputContext; modelId: string; gdvEditor?: GdvEditingContext; checks: ReturnType<typeof checksById>
}) {
  // Authored section keys are free text; the heading id is generated.
  const id = useId()
  const lines = section.items.flatMap(item => {
    if (item.kind === 'input') {
      const input = layout.inputs.find(candidate => candidate.key === item.inputKey)
      return input ? [<InputLine key={`i:${input.key}`} input={input} context={context} />] : []
    }
    const output: PresentationOutput | undefined = lookups.outputs.get(item.outputId)
    if (!output || item.role === 'internal') return []
    if (item.role === 'check') return [<CheckLine key={`r:${output.outputId}`} row={checkRow(output.outputId, layout, lookups, context, checks)} />]
    const raw = lookups.value(output.outputId)
    return [<div className={item.role === 'summary' ? 'fin-row is-summary' : 'fin-row'} key={`r:${output.outputId}`}>
      <dt>{output.label}</dt><dd><FinancialOutputValue modelId={modelId} outputId={output.outputId} value={formatOutput(raw, output)} rawValue={raw} gdvEditor={gdvEditor} /></dd>
    </div>]
  })
  if (!lines.length) return null
  return <section className="panel fin-sec" aria-labelledby={id}>
    <div className="panel-h"><h4 className="t-section" id={id}>{section.label}</h4></div>
    {section.description ? <p className="fin-faint">{section.description}</p> : null}
    <dl className="fin-sec-rows">{lines}</dl>
  </section>
}

function Sections({ layout, lookups, context, modelId, gdvEditor }: { layout: DealModelLayout; lookups: ResultLookups; context: InputContext; modelId: string; gdvEditor?: GdvEditingContext }) {
  const checks = checksById(layout)
  const ungrouped = layout.ungroupedOutputIds.filter(id => lookups.outputs.has(id))
  if (!layout.sections.length && !ungrouped.length) return null
  return <div className="fin-sections">
    {layout.sections.map(section => <SectionPanel key={section.key} section={section} layout={layout} lookups={lookups} context={context} modelId={modelId} gdvEditor={gdvEditor} checks={checks} />)}
    {ungrouped.length ? <SectionPanel
      // The contract gives ungrouped rows no role: only the layout's own checks are shown as checks.
      section={{ key: 'other-outputs', label: 'Other outputs', description: null, items: ungrouped.map(outputId => ({ kind: 'row' as const, outputId, role: checks.has(outputId) ? 'check' as const : 'detail' as const })) }}
      layout={layout} lookups={lookups} context={context} modelId={modelId} gdvEditor={gdvEditor} checks={checks}
    /> : null}
  </div>
}

/* ---------- ledger at the maximum bid ---------- */

/** Every presentation output at the calculation price and at the maximum bid. */
export function BidLedgerTable({ result, evaluation, modelId, gdvEditor }: { result: FinancialModelResult; evaluation: BidEvaluation; modelId: string; gdvEditor?: GdvEditingContext }) {
  const { profile, bid } = evaluation
  return <div className="fin-bd" role="table" aria-label={`${humanize(profile)} calculation outputs`}>
    <div role="row" className="fin-bd-row is-head"><span role="columnheader">Calculation</span><span role="columnheader">Listing comparison</span><span role="columnheader">At maximum bid</span></div>
    {result.presentation.outputs.map(output => {
      const atPrice = valueAt(result.calculationPriceResult === undefined ? result.result : result.calculationPriceResult, output.pointer)
      const atMax = bid.state === 'computed' && bid.maxBidPence !== null ? valueAt(bid.atMaximum, output.pointer) : undefined
      return <div role="row" className="fin-bd-row" key={output.outputId}>
        <span role="rowheader">{output.label}</span>
        <span role="cell"><FinancialOutputValue modelId={modelId} outputId={output.outputId} value={formatOutput(atPrice, output)} rawValue={atPrice} gdvEditor={gdvEditor} /></span>
        <span role="cell"><FinancialOutputValue modelId={modelId} outputId={output.outputId} value={formatOutput(atMax, output)} rawValue={atMax} gdvEditor={gdvEditor} /></span>
      </div>
    })}
  </div>
}

function MaxBidLedger({ model, layout, result, evaluation, lookups, modelId, gdvEditor }: {
  model: Pick<DealsV2PropertyModelChoice, 'ownership'>; layout: DealModelLayout; result: FinancialModelResult; evaluation: BidEvaluation; lookups: ResultLookups; modelId: string; gdvEditor?: GdvEditingContext
}) {
  const constraint = bidConstraint(layout, lookups, evaluation)
  return <details className="fin-disclosure">
    <summary>Compare with the listing price</summary>
    <div className="fin-bid">
      <p className="fin-faint">{humanize(evaluation.profile)} works. The maximum-bid column supplies the deal figures above. The listing-price column is a separate comparison.</p>
      {constraint ? <p className="fin-faint">{constraint.label}: {constraint.value}</p> : null}
      <BidLedgerTable result={result} evaluation={evaluation} modelId={modelId} gdvEditor={gdvEditor} />
      <p className="fin-faint">{evaluation.bid.state === 'computed' ? 'One penny above this maximum no longer meets the model targets.' : bidStateLabel(evaluation.bid.state)}</p>
      <p className="fin-faint">{model.ownership === 'builtIn' ? 'Before income/corporation tax · England/NI residential SDLT · assumes chosen works achieve the estimated resale value/rent.' : 'Verify this model’s tax, fee and works assumptions before bidding.'}</p>
    </div>
  </details>
}

/* ---------- the view: summary (band + KPIs) above the notices, details (sections + ledger) below ---------- */

export interface DealViewProps {
  model: DealsV2PropertyModelChoice
  layout: DealModelLayout
  result: FinancialModelResult
  lookups: ResultLookups
  context: InputContext
  gdvEditor?: GdvEditingContext
}

export function DealSummary({ model, layout, result, lookups, context, status, notice, pricePence, priceLabel, gdvEditor, resultFreshness, staleReasons = [], recalculating = false, hideTargetKpis }: DealViewProps & {
  status: ReactNode
  notice?: ReactNode
  pricePence: number | null | undefined
  priceLabel: string
  resultFreshness: 'current' | 'updating'
  /** Why the saved result is not the latest calculation; empty from an older server. */
  staleReasons?: readonly string[]
  /** The page knows a recalculation is in progress. */
  recalculating?: boolean
  hideTargetKpis?: boolean
}) {
  const updating = resultFreshness === 'updating'
  return <>
    <VerdictBand layout={layout} result={result} lookups={lookups} context={context} priceLabel={priceLabel} status={status} notice={notice} updating={updating} staleReasons={staleReasons} recalculating={recalculating} />
    <KpiStrip layout={layout} result={result} lookups={lookups} modelId={model.logicalModelId} gdvEditor={gdvEditor} pricePence={pricePence} priceLabel={priceLabel} updating={updating} hideTargetKpis={hideTargetKpis} />
  </>
}

export function DealDetails({ model, layout, result, lookups, context, gdvEditor }: DealViewProps) {
  const modelId = model.logicalModelId
  return <>
    <Sections layout={layout} lookups={lookups} context={context} modelId={modelId} gdvEditor={gdvEditor} />
    {result.bidEvaluation ? <MaxBidLedger model={model} layout={layout} result={result} evaluation={result.bidEvaluation} lookups={lookups} modelId={modelId} gdvEditor={gdvEditor} /> : null}
  </>
}
